/**
 * 数据同步工具 - 提供LocalStorage和D1数据库之间的数据同步功能
 * 支持数据导入、导出、增量同步和批处理操作
 */

// 导入压缩库
import * as pako from 'pako';

// 添加获取认证令牌的辅助函数
// 认证头缓存机制
interface AuthHeaderCache {
  headers: HeadersInit | null;
  token: string | null;
  timestamp: number;
  isValid: boolean;
}

let authCache: AuthHeaderCache = {
  headers: null,
  token: null,
  timestamp: 0,
  isValid: false
};

// 认证头缓存有效期（5分钟）
const AUTH_CACHE_TIMEOUT = 5 * 60 * 1000;

// 优化的认证头获取函数 - 支持缓存和批量会话
const getAuthorizationHeader = (useCache: boolean = true): HeadersInit => {
  const now = Date.now();
  const currentToken = localStorage.getItem('auth_token');
  
  // 检查缓存是否有效
  if (useCache && authCache.isValid && 
      authCache.token === currentToken && 
      (now - authCache.timestamp) < AUTH_CACHE_TIMEOUT &&
      authCache.headers) {
    // 使用缓存的认证头，避免重复处理
    return authCache.headers;
  }
  
  // 重新构建认证头
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };
  
  // 只在第一次获取或令牌变化时输出详细日志
  const shouldLogDetail = !authCache.isValid || authCache.token !== currentToken;
  
  if (shouldLogDetail) {
    console.log('🔐 认证令牌获取:', {
      tokenExists: !!currentToken,
      tokenLength: currentToken ? currentToken.length : 0,
      tokenPrefix: currentToken ? currentToken.substring(0, 15) + '...' : '无',
      cacheStatus: authCache.isValid ? '缓存失效' : '首次获取'
    });
  }
  
  if (currentToken) {
    headers['Authorization'] = `Bearer ${currentToken}`;
    
    if (shouldLogDetail) {
      console.log('✅ 认证头已配置');
    }
    
    // 更新缓存
    authCache = {
      headers: { ...headers },
      token: currentToken,
      timestamp: now,
      isValid: true
    };
  } else {
    console.warn('⚠️  未找到认证令牌，可能无法访问受保护的API');
    authCache.isValid = false;
  }
  
  return headers;
};

// 批量操作专用的认证头获取 - 一次性获取，整个批量操作期间复用
const getBatchAuthorizationHeader = (): HeadersInit => {
  console.log('🚀 批量操作：获取认证头...');
  return getAuthorizationHeader(false); // 强制刷新以确保最新
};

// 清除认证缓存（当令牌更新时调用）
const clearAuthCache = () => {
  authCache = {
    headers: null,
    token: null,
    timestamp: 0,
    isValid: false
  };
  console.log('🔄 认证缓存已清除');
};

// 检查认证状态
const checkAuthStatus = (): boolean => {
  const token = localStorage.getItem('auth_token');
  return !!token;
};

// 添加调试函数 - 可以在浏览器控制台中调用
export const debugAuthToken = () => {
  const tokenInfo = {
    auth_token: localStorage.getItem('auth_token'),
    auth_user: localStorage.getItem('auth_user'),
    refresh_token: localStorage.getItem('refresh_token')
  };
  
  console.log('认证令牌调试信息:', tokenInfo);
  
  // 测试API连接
  fetch('https://akayd1.ggff.net/api/auth/status')
    .then(res => res.json())
    .then(data => {
      console.log('API状态检查结果:', data);
      
      // 测试带认证的请求
      const headers = getAuthorizationHeader(false); // 强制刷新以获取最新状态
      return fetch('https://akayd1.ggff.net/api/d1/students', {
        headers
      });
    })
    .then(res => {
      console.log('D1 API测试结果:', {
        status: res.status,
        statusText: res.statusText
      });
      return res.json();
    })
    .then(data => {
      console.log('D1 API测试数据:', data);
    })
    .catch(err => {
      console.error('API测试错误:', err);
    });
    
  return '调试信息已输出到控制台';
};

// 保存操作状态，使这些函数可以被应用中的任何组件访问
let isImportingData = false;
let isExportingData = false;
// 添加进度跟踪状态
let syncProgressState = {
  current: 0,
  total: 0,
  currentCollection: '',
  status: 'idle', // 'idle', 'running', 'paused', 'error', 'completed'
  startTime: 0,
  endTime: 0,
  processingSpeed: 0,
  errorDetails: [] as any[],
  currentBatch: 0,
  totalBatches: 0,
  syncType: 'full' as 'full' | 'incremental' // 新增：同步类型
};

// 记录上次同步时间，用于增量同步
interface SyncTimestamps {
  [collection: string]: string;
}

let lastSyncTimestamps: SyncTimestamps = {};
let lastExportTimestamps: SyncTimestamps = {};

// 尝试从localStorage加载上次同步时间
try {
  const savedTimestamps = localStorage.getItem('lastSyncTimestamps');
  if (savedTimestamps) {
    lastSyncTimestamps = JSON.parse(savedTimestamps);
    console.log('已从localStorage加载上次同步时间:', lastSyncTimestamps);
  }
} catch (e) {
  console.warn('读取上次同步时间失败:', e);
}

// 尝试从localStorage加载上次导出时间
try {
  const savedExportTimestamps = localStorage.getItem('lastExportTimestamps');
  if (savedExportTimestamps) {
    lastExportTimestamps = JSON.parse(savedExportTimestamps);
    console.log('已从localStorage加载上次导出时间:', lastExportTimestamps);
  }
} catch (e) {
  console.warn('读取上次导出时间失败:', e);
}

// 保存同步时间戳到localStorage
const saveTimestamps = () => {
  try {
    localStorage.setItem('lastSyncTimestamps', JSON.stringify(lastSyncTimestamps));
    localStorage.setItem('lastExportTimestamps', JSON.stringify(lastExportTimestamps));
  } catch (e) {
    console.warn('保存同步时间戳失败:', e);
  }
};

// 新增：为本地数据添加时间戳
// 检查本地数据是否有变更
const getLocalChanges = (collection: string, lastExportTime?: string): any[] => {
  try {
    const dataStr = localStorage.getItem(collection);
    if (!dataStr) return [];
    
    const data = JSON.parse(dataStr);
    if (!Array.isArray(data)) return [];
    
    if (!lastExportTime) return data; // 没有上次导出时间，返回所有数据
    
    // 返回在上次导出时间之后修改的数据
    return data.filter(item => {
      const modifyTime = item.last_modified_locally || item.updated_at || item.created_at;
      return modifyTime && new Date(modifyTime) > new Date(lastExportTime);
    });
  } catch (e) {
    console.warn(`检查${collection}本地变更时出错:`, e);
    return [];
  }
};

// 新增：数据一致性检查
const checkDataConsistency = async (collection: string): Promise<{
  consistent: boolean;
  localCount: number;
  remoteCount: number;
  conflicts: any[];
}> => {
  try {
    // 获取本地数据
    const localDataStr = localStorage.getItem(collection);
    const localData = localDataStr ? JSON.parse(localDataStr) : [];
    
    // 获取远程数据（全量）
    const response = await fetch(`https://akayd1.ggff.net/api/d1/${collection}`, {
      headers: getAuthorizationHeader(true) // 使用缓存
    });
    
    if (!response.ok) {
      throw new Error(`获取远程数据失败: ${response.status}`);
    }
    
    const result = await response.json();
    const remoteData = result.success ? (result.data || []) : [];
    
    // 创建ID映射进行比较
    const localMap = new Map();
    const remoteMap = new Map();
    
    (Array.isArray(localData) ? localData : []).forEach(item => {
      if (item.id) localMap.set(item.id, item);
    });
    
    (Array.isArray(remoteData) ? remoteData : []).forEach(item => {
      if (item.id) remoteMap.set(item.id, item);
    });
    
    // 检查冲突
    const conflicts: any[] = [];
    
    // 使用Array.from来避免迭代器兼容性问题
    Array.from(localMap.entries()).forEach(([id, localItem]) => {
      const remoteItem = remoteMap.get(id);
      if (remoteItem) {
        const localTime = new Date(localItem.updated_at || localItem.created_at || 0);
        const remoteTime = new Date(remoteItem.updated_at || remoteItem.created_at || 0);
        
        // 检查数据是否不一致
        const localContent = JSON.stringify({...localItem, updated_at: undefined, created_at: undefined});
        const remoteContent = JSON.stringify({...remoteItem, updated_at: undefined, created_at: undefined});
        
        if (localContent !== remoteContent) {
          conflicts.push({
            id,
            type: 'content_conflict',
            localItem,
            remoteItem,
            localTime: localTime.toISOString(),
            remoteTime: remoteTime.toISOString(),
            resolution: localTime > remoteTime ? 'use_local' : 'use_remote'
          });
        }
      } else {
        // 本地有但远程没有
        conflicts.push({
          id,
          type: 'local_only',
          localItem,
          resolution: 'upload_to_remote'
        });
      }
    });
    
    Array.from(remoteMap.entries()).forEach(([id, remoteItem]) => {
      if (!localMap.has(id)) {
        // 远程有但本地没有
        conflicts.push({
          id,
          type: 'remote_only',
          remoteItem,
          resolution: 'download_to_local'
        });
      }
    });
    
    return {
      consistent: conflicts.length === 0,
      localCount: localMap.size,
      remoteCount: remoteMap.size,
      conflicts
    };
  } catch (error) {
    console.error(`检查${collection}数据一致性时出错:`, error);
    throw error;
  }
};

// 优化的压缩数据函数 (如果pako不可用则不压缩)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const compressData = (data: any): { compressed: boolean, data: any, originalSize?: number, compressedSize?: number } => {
  try {
    // 使用pako库进行压缩
    if (typeof pako !== 'undefined') {
      const jsonString = JSON.stringify(data);
      const originalSize = jsonString.length;
      
      // 只对大于5KB的数据进行压缩
      const sizeThreshold = 1024 * 5; // 5KB
      if (originalSize < sizeThreshold) {
        return { compressed: false, data, originalSize };
      }
      
      // TypeScript修复：确保压缩数据是Uint8Array，使用较快的gzip
      const compressed = pako.gzip(jsonString, { level: 6 }) as Uint8Array; // 平衡压缩率和速度
      
      // 使用浏览器兼容的方式而不是Node.js的Buffer
      // 将Uint8Array转换为Base64字符串
      const base64Compressed = btoa(
        Array.from(compressed)
          .map(byte => String.fromCharCode(byte))
          .join('')
      );
      
      const compressedSize = base64Compressed.length;
      
      // 如果压缩后反而更大，使用原始数据
      if (compressedSize >= originalSize) {
        return { compressed: false, data, originalSize };
      }
      
      const compressionRatio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
      console.log(`数据压缩: ${originalSize} -> ${compressedSize} 字节 (节省 ${compressionRatio}%)`);
      
      return { 
        compressed: true, 
        data: base64Compressed,
        originalSize,
        compressedSize
      };
    }
    return { compressed: false, data }; // 默认不压缩
  } catch (e) {
    console.warn('压缩数据失败，使用未压缩数据:', e);
    return { compressed: false, data };
  }
};

// 解压数据函数 (如果pako不可用则假设数据未压缩)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const decompressData = (payload: { compressed: boolean, data: any }): any => {
  try {
    if (payload.compressed) {
      // 使用浏览器兼容的方式而不是Node.js的Buffer
      // 将Base64字符串转换为Uint8Array
      const binaryString = atob(payload.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // 使用pako库进行解压缩，匹配gzip压缩方式
      const decompressed = pako.ungzip(bytes) as Uint8Array;
      const jsonString = new TextDecoder().decode(decompressed);
      return JSON.parse(jsonString);
    }
    return payload.data; // 数据未压缩，直接返回
  } catch (e) {
    console.error('解压数据失败:', e);
    throw e;
  }
};

// 改进的数据合并函数，支持冲突解决
const mergeDataWithConflictResolution = (existingData: any[], newData: any[], resolveConflict: 'use_local' | 'use_remote' | 'use_newer' = 'use_newer'): {
  merged: any[];
  conflicts: any[];
} => {
  if (!Array.isArray(existingData) || !Array.isArray(newData)) {
    throw new Error('合并数据时数据类型错误：需要数组类型');
  }
  
  const conflicts: any[] = [];
  const idMap = new Map();
  const duplicateIds = new Set();
  
  // 检查现有数据中的重复ID
  existingData.forEach((item, index) => {
    if (item && item.id) {
      if (idMap.has(item.id)) {
        duplicateIds.add(item.id);
        console.warn(`检测到现有数据中的重复ID: ${item.id} (索引: ${index})`);
        
        // 保留较新的记录
        const existing = idMap.get(item.id);
        const existingTime = new Date(existing.updated_at || existing.created_at || 0);
        const currentTime = new Date(item.updated_at || item.created_at || 0);
        
        if (currentTime > existingTime) {
          console.log(`替换重复ID ${item.id} 的较旧记录`);
          idMap.set(item.id, item);
        }
      } else {
        idMap.set(item.id, item);
      }
    } else {
      // 为缺少ID的记录生成新ID
      if (item) {
        const newId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        item.id = newId;
        idMap.set(newId, item);
        console.log(`为记录生成新ID: ${newId}`);
      }
    }
  });
  
  // 处理新数据
  newData.forEach((newItem, index) => {
    if (newItem && newItem.id) {
      const existingItem = idMap.get(newItem.id);
      
      if (existingItem) {
        // 检查是否有实质性内容冲突（排除时间戳字段）
        const existingContent = JSON.stringify({
          ...existingItem, 
          updated_at: undefined, 
          created_at: undefined,
          last_modified_locally: undefined
        });
        const newContent = JSON.stringify({
          ...newItem, 
          updated_at: undefined, 
          created_at: undefined,
          last_modified_locally: undefined
        });
        
        if (existingContent !== newContent) {
          // 有实质性冲突，根据策略解决
          const existingTime = new Date(existingItem.updated_at || existingItem.last_modified_locally || existingItem.created_at || 0);
          const newTime = new Date(newItem.updated_at || newItem.last_modified_locally || newItem.created_at || 0);
          
          let resolvedItem;
          
          switch (resolveConflict) {
            case 'use_local':
              resolvedItem = existingItem;
              break;
            case 'use_remote':
              resolvedItem = newItem;
              break;
            case 'use_newer':
            default:
              // 如果时间相同，优先使用本地数据
              resolvedItem = newTime > existingTime ? newItem : existingItem;
              break;
          }
          
          conflicts.push({
            id: newItem.id,
            existingItem,
            newItem,
            resolution: resolveConflict,
            resolvedItem,
            contentDifferent: true
          });
          
          idMap.set(newItem.id, resolvedItem);
          console.log(`解决内容冲突 ID: ${newItem.id}, 策略: ${resolveConflict}`);
        } else {
          // 内容相同，但可能时间戳不同，更新时间戳为较新的
          const existingTime = new Date(existingItem.updated_at || existingItem.created_at || 0);
          const newTime = new Date(newItem.updated_at || newItem.created_at || 0);
          
          if (newTime > existingTime) {
            const updatedItem = {
              ...existingItem,
              updated_at: newItem.updated_at,
              last_modified_locally: newItem.last_modified_locally
            };
            idMap.set(newItem.id, updatedItem);
          }
        }
      } else {
        // 新记录，直接添加
        idMap.set(newItem.id, newItem);
      }
    } else {
      // 为缺少ID的新记录生成ID
      if (newItem) {
        const newId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        newItem.id = newId;
        idMap.set(newId, newItem);
        console.log(`为新记录生成ID: ${newId} (新数据索引: ${index})`);
      }
    }
  });
  
  const merged = Array.from(idMap.values());
  
  // 额外的重复检查
  const finalIdCheck = new Set<string>();
  const finalDuplicates: string[] = [];
  merged.forEach(item => {
    if (finalIdCheck.has(item.id)) {
      finalDuplicates.push(item.id);
    } else {
      finalIdCheck.add(item.id);
    }
  });
  
  if (finalDuplicates.length > 0) {
    console.error('合并后仍存在重复ID:', finalDuplicates);
  }
  
  console.log(`数据合并完成: 原始${existingData.length + newData.length}条, 合并后${merged.length}条, 冲突${conflicts.length}条, 重复ID处理${duplicateIds.size}个`);
  
  return {
    merged,
    conflicts
  };
};

// 辅助函数：合并数据（用于增量同步）- 保持向后兼容
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mergeData = (existingData: any[], newData: any[]): any[] => {
  const result = mergeDataWithConflictResolution(existingData, newData, 'use_newer');
  return result.merged;
};

export const getDataSyncStatus = () => {
  return {
    ...syncProgressState,
    isImporting: isImportingData,
    isExporting: isExportingData,
    totalDuration: syncProgressState.endTime > 0 
      ? syncProgressState.endTime - syncProgressState.startTime 
      : syncProgressState.startTime > 0 
        ? Date.now() - syncProgressState.startTime 
        : 0,
    estimatedTimeRemaining: syncProgressState.processingSpeed > 0 && syncProgressState.total > 0
      ? Math.round((syncProgressState.total - syncProgressState.current) / syncProgressState.processingSpeed)
      : 0
  };
};

export const cancelDataSync = () => {
  if (isImportingData || isExportingData) {
    syncProgressState.status = 'paused';
    console.log('数据同步已被用户取消');
    isImportingData = false;
    isExportingData = false;
    return true;
  }
  return false;
};

export const importFromD1 = async (useIncremental = false, options: {
  checkConsistency?: boolean;
  conflictResolution?: 'use_local' | 'use_remote' | 'use_newer';
} = {}): Promise<{
  success: boolean;
  message: string;
  stats: {
    totalRecords: number;
    successCount: number;
    errorCount: number;
    conflicts?: any[];
    consistencyCheck?: any;
  };
}> => {
  if (isImportingData) {
    return {
      success: false,
      message: '已有导入操作正在进行中',
      stats: { totalRecords: 0, successCount: 0, errorCount: 0 }
    };
  }
  
  const { checkConsistency = false, conflictResolution = 'use_newer' } = options;
  
  isImportingData = true;
  // 重置进度状态
  syncProgressState = {
    current: 0,
    total: 0,
    currentCollection: '',
    status: 'running',
    startTime: Date.now(),
    endTime: 0,
    processingSpeed: 0,
    errorDetails: [],
    currentBatch: 0,
    totalBatches: 0,
    syncType: useIncremental ? 'incremental' : 'full'
  };
  
  try {
    // 在导入开始时获取并缓存认证头
    console.log('🔐 初始化导入会话认证...');
    const sessionAuthHeaders = getBatchAuthorizationHeader();
    
    if (!checkAuthStatus()) {
      throw new Error('认证失败：未找到有效的认证令牌');
    }
    
    // 先检查API是否能够正常连接
    const testResponse = await fetch('https://akayd1.ggff.net/api/auth/status', {
      headers: sessionAuthHeaders
    });
    if (!testResponse.ok) {
      throw new Error(`API连接测试失败: ${testResponse.status} ${testResponse.statusText}`);
    }
    
    console.log('✅ 导入会话认证成功，开始批量获取数据...');
    
    // 获取数据集合列表
    const collections = [
      'students', 
      'classes', 
      'attendance', 
      'teacher_attendance',
      'grades',
      'payments', 
      'subjects', 
      'schoolYears', 
      'teachers',
      'custom_reports',
      'alert_rules',
      'preferences',
      'custom_exams',
      'current_context'
    ];
    
    let successCount = 0;
    let errorCount = 0;
    let totalRecords = 0;
    let allConflicts: any[] = [];
    let consistencyResults: any = {};
    
    console.log(`开始${useIncremental ? '增量' : '全量'}同步，共${collections.length}个集合`);
    
    // 逐个获取每个集合的数据并存入localStorage
    for (const collection of collections) {
      try {
        // 更新当前处理的集合名称
        syncProgressState.currentCollection = collection;
        
        console.log(`开始从D1数据库获取${collection}数据 (${useIncremental ? '增量' : '全量'}模式)...`);
        
        let response;
        let apiUrl;
        
        if (useIncremental && lastSyncTimestamps[collection]) {
          // 使用增量API
          apiUrl = `https://akayd1.ggff.net/api/incremental/${collection}?since=${encodeURIComponent(lastSyncTimestamps[collection])}`;
          console.log(`使用增量API，上次同步时间: ${lastSyncTimestamps[collection]}`);
        } else {
          // 使用全量API
          apiUrl = `https://akayd1.ggff.net/api/d1/${collection}`;
          console.log(`使用全量API (${useIncremental ? '首次增量同步' : '全量同步'})`);
        }
        
        response = await fetch(apiUrl, {
          headers: sessionAuthHeaders
        });
        
        if (!response.ok) {
          errorCount++;
          console.error(`获取${collection}数据失败: ${response.status} ${response.statusText}`);
          syncProgressState.errorDetails.push({
            collection,
            error: `HTTP ${response.status}: ${response.statusText}`,
            timestamp: new Date().toISOString()
          });
          continue;
        }
        
        const result = await response.json();
        
        // 处理响应数据
        let dataToStore;
        let recordCount = 0;
        let currentServerTimestamp;
        
        if (useIncremental && result.currentTimestamp) {
          // 增量API响应格式
          dataToStore = result.data || [];
          recordCount = dataToStore.length;
          currentServerTimestamp = result.currentTimestamp;
          console.log(`增量API返回${recordCount}条新记录，服务器时间戳: ${currentServerTimestamp}`);
        } else if (result.success && result.data) {
          // 全量API响应格式
          dataToStore = result.data;
          recordCount = Array.isArray(dataToStore) ? dataToStore.length : 1;
          currentServerTimestamp = new Date().toISOString();
        } else {
          console.warn(`${collection}数据为空或格式异常`);
          continue;
        }
        
        // 数据存储逻辑
        if (dataToStore && Array.isArray(dataToStore)) {
          if (useIncremental && recordCount > 0) {
            // 增量同步：合并现有数据
            const existingDataStr = localStorage.getItem(collection);
            if (existingDataStr) {
              try {
                const existingData = JSON.parse(existingDataStr);
                if (Array.isArray(existingData)) {
                  const mergeResult = mergeDataWithConflictResolution(existingData, dataToStore, conflictResolution);
                  localStorage.setItem(collection, JSON.stringify(mergeResult.merged));
                  
                  if (mergeResult.conflicts.length > 0) {
                    console.log(`${collection}发现${mergeResult.conflicts.length}个冲突，已使用${conflictResolution}策略解决`);
                    allConflicts.push(...mergeResult.conflicts.map(c => ({ ...c, collection })));
                  }
                  
                  console.log(`已将${recordCount}条${collection}数据增量导入localStorage，合并后共${mergeResult.merged.length}条`);
                } else {
                  // 现有数据不是数组，直接替换
                  localStorage.setItem(collection, JSON.stringify(dataToStore));
                  console.log(`${collection}现有数据格式异常，已全量替换`);
                }
              } catch (e) {
                // 解析现有数据失败，直接替换
                localStorage.setItem(collection, JSON.stringify(dataToStore));
                console.log(`${collection}现有数据解析失败，已全量替换`);
              }
            } else {
              // 没有现有数据，直接存储
              localStorage.setItem(collection, JSON.stringify(dataToStore));
              console.log(`已将${recordCount}条${collection}数据初始导入localStorage`);
            }
          } else if (!useIncremental || recordCount > 0) {
            // 全量同步或有新数据：直接替换
            localStorage.setItem(collection, JSON.stringify(dataToStore));
            console.log(`已将${recordCount}条${collection}数据导入localStorage (${useIncremental ? '增量' : '全量'}更新)`);
          } else {
            console.log(`${collection}无新数据，跳过更新`);
          }
          
          successCount++;
          totalRecords += recordCount;
        } else if (dataToStore && typeof dataToStore === 'object') {
          // 处理非数组对象
          localStorage.setItem(collection, JSON.stringify(dataToStore));
          successCount++;
          totalRecords++;
          console.log(`已将${collection}数据对象导入localStorage`);
        }
        
        // 更新进度
        syncProgressState.current++;
        
        // 记录同步时间戳（使用服务器时间戳）
        lastSyncTimestamps[collection] = currentServerTimestamp;
        
        // 可选的一致性检查
        if (checkConsistency) {
          try {
            const consistencyCheck = await checkDataConsistency(collection);
            consistencyResults[collection] = consistencyCheck;
            if (!consistencyCheck.consistent) {
              console.warn(`${collection}数据一致性检查失败，发现${consistencyCheck.conflicts.length}个冲突`);
            }
          } catch (error) {
            console.warn(`${collection}一致性检查失败:`, error);
          }
        }
        
      } catch (error) {
        errorCount++;
        console.error(`处理${collection}数据时出错:`, error);
        syncProgressState.errorDetails.push({
          collection,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString()
        });
      }
    }
    
    // 保存最新的同步时间戳
    saveTimestamps();
    
    // 🔧 修复：清除所有缓存，确保学生考勤等功能能获取最新的同步数据
    console.log('🧹 清除缓存，确保数据同步后立即生效...');
    try {
      // 导入StorageManager来清除缓存
      const { storageManager } = await import('./storage');
      storageManager.clearAllCache();
      console.log('✅ 缓存清除完成');
    } catch (error) {
      console.error('清除缓存失败:', error);
      // 不影响主要功能，只记录错误
    }
    
    // 触发数据变更事件
    window.dispatchEvent(new Event('storageDataChanged'));
    
    // 更新最终进度状态
    syncProgressState.status = 'completed';
    syncProgressState.endTime = Date.now();
    
    // 生成结果消息
    let message = `${useIncremental ? '增量' : '全量'}数据导入完成！成功导入${successCount}个集合，共${totalRecords}条记录`;
    if (errorCount > 0) {
      message += `，${errorCount}个集合失败`;
    }
    if (allConflicts.length > 0) {
      message += `，解决了${allConflicts.length}个数据冲突`;
    }
    
    // 返回结果
    return {
      success: true,
      message,
      stats: { 
        totalRecords, 
        successCount, 
        errorCount,
        conflicts: allConflicts.length > 0 ? allConflicts : undefined,
        consistencyCheck: checkConsistency ? consistencyResults : undefined
      }
    };
  } catch (error) {
    // 更新错误状态
    syncProgressState.status = 'error';
    syncProgressState.endTime = Date.now();
    
    return {
      success: false,
      message: `导入数据失败: ${error instanceof Error ? error.message : '未知错误'}`,
      stats: { totalRecords: 0, successCount: 0, errorCount: 0 }
    };
  } finally {
    isImportingData = false;
  }
};

export const exportToD1 = async (options: {
  useCompression?: boolean;
  batchStrategy?: 'adaptive' | 'fixed';
  fixedBatchSize?: number;
  retryPolicy?: 'aggressive' | 'moderate' | 'conservative';
  useIncremental?: boolean;
  checkConsistency?: boolean;
} = {}): Promise<{
  success: boolean;
  message: string;
  stats: {
    totalRecords: number;
    successCount: number;
    errorCount: number;
    failedCollections?: string[];
    errors?: any[];
    retryCount?: number;
    compressionEnabled?: boolean;
    compressionStats?: any;
    batchingStats?: any;
    consistencyCheck?: any;
  };
}> => {
  if (isExportingData) {
    return {
      success: false,
      message: '已有导出操作正在进行中',
      stats: { totalRecords: 0, successCount: 0, errorCount: 0 }
    };
  }
  
  // 设置默认选项
  const {
    useCompression = true, // 默认启用压缩以提高传输效率
    batchStrategy = 'adaptive',
    fixedBatchSize = 200, // 提高默认批次大小
    retryPolicy = 'moderate',
    useIncremental = false,
    checkConsistency = false
  } = options;
  
  isExportingData = true;
  // 重置进度状态
  syncProgressState = {
    current: 0,
    total: 0,
    currentCollection: '',
    status: 'running',
    startTime: Date.now(),
    endTime: 0,
    processingSpeed: 0,
    errorDetails: [],
    currentBatch: 0,
    totalBatches: 0,
    syncType: 'full'
  };
  
  // 设置重试策略
  const maxRetries = 
    retryPolicy === 'aggressive' ? 5 : 
    retryPolicy === 'conservative' ? 2 : 3;  // moderate default
  
  const retryDelayBase = 
    retryPolicy === 'aggressive' ? 500 : 
    retryPolicy === 'conservative' ? 2000 : 1000; // moderate default
  
  // 跟踪压缩统计
  const compressionStats = {
    enabled: useCompression,
    totalSizeBefore: 0,
    totalSizeAfter: 0,
    compressionRatio: 0
  };
  
  // 跟踪批处理策略统计
  const batchingStats = {
    strategy: batchStrategy,
    initialBatchSize: fixedBatchSize,
    batchSizeAdjustments: 0,
    smallestBatchSize: fixedBatchSize,
    largestBatchSize: fixedBatchSize,
    averageBatchSize: 0,
    totalBatches: 0
  };
  
  try {
    // 在同步开始时获取并缓存认证头，避免每次请求重复获取
    console.log('🔐 初始化同步会话认证...');
    const sessionAuthHeaders = getBatchAuthorizationHeader();
    
    if (!checkAuthStatus()) {
      syncProgressState.status = 'error';
      throw new Error('认证失败：未找到有效的认证令牌');
    }
    
    // 先检查API是否能够正常连接
    const testResponse = await fetch('https://akayd1.ggff.net/api/auth/status', {
      headers: sessionAuthHeaders
    });
    if (!testResponse.ok) {
      syncProgressState.status = 'error';
      throw new Error(`API连接测试失败: ${testResponse.status} ${testResponse.statusText}`);
    }
    
    console.log('✅ 同步会话认证成功，开始批量操作...');
    
    // 获取数据集合列表
    const collections = [
      'students', 
      'classes', 
      'attendance', 
      'teacher_attendance',
      'grades',
      'payments', 
      'subjects', 
      'schoolYears', 
      'teachers',
      'custom_reports',
      'alert_rules',
      'preferences',
      'custom_exams',
      'current_context'
    ];
    
    let successCount = 0;
    let errorCount = 0;
    let totalRecords = 0;
    let totalRetries = 0;
    let totalBatchesProcessed = 0;
    let sumBatchSize = 0;
    
    const failedCollections: string[] = [];
    const errors: any[] = [];
    
    // 预计算总记录数用于进度显示
    let estimatedTotalRecords = 0;
    for (const collection of collections) {
      const dataStr = localStorage.getItem(collection);
      if (dataStr) {
        try {
          const data = JSON.parse(dataStr);
          if (Array.isArray(data)) {
            estimatedTotalRecords += data.length;
          } else if (data) {
            estimatedTotalRecords += 1;
          }
        } catch (e) {
          console.warn(`计算${collection}记录数时出错:`, e);
        }
      }
    }
    
    syncProgressState.total = estimatedTotalRecords;
    syncProgressState.syncType = useIncremental ? 'incremental' : 'full';
    
    console.log(`开始${useIncremental ? '增量' : '全量'}导出，共${collections.length}个集合`);
    
    // 逐个处理每个集合，将localStorage中的数据发送到D1数据库
    for (const collection of collections) {
      try {
        syncProgressState.currentCollection = collection;
        
        // 从localStorage获取数据
        const dataStr = localStorage.getItem(collection);
        if (!dataStr) {
          console.log(`localStorage中没有${collection}数据，跳过`);
          continue;
        }
        
        const data = JSON.parse(dataStr);
        if (!data) {
          console.log(`${collection}数据为空，跳过`);
          continue;
        }
        
        // 获取需要导出的数据
        let dataToExport;
        if (useIncremental) {
          // 增量模式：只导出有变更的数据
          if (Array.isArray(data)) {
            dataToExport = getLocalChanges(collection, lastExportTimestamps[collection]);
            if (dataToExport.length === 0) {
              console.log(`${collection}无本地变更，跳过增量导出`);
              continue;
            }
            console.log(`${collection}发现${dataToExport.length}条本地变更记录，准备增量导出`);
          } else {
            // 对于非数组类型（如preferences），检查修改时间
            const lastExportTime = lastExportTimestamps[collection];
            const modifyTime = data.last_modified_locally || data.updated_at || data.created_at;
            
            if (lastExportTime && modifyTime && new Date(modifyTime) <= new Date(lastExportTime)) {
              console.log(`${collection}对象无变更，跳过增量导出`);
              continue;
            }
            dataToExport = data;
            console.log(`${collection}对象有变更，准备增量导出`);
          }
        } else {
          // 全量模式：导出所有数据
          dataToExport = data;
          console.log(`${collection}准备全量导出: ${Array.isArray(dataToExport) ? dataToExport.length : 1}条记录`);
        }
        
        // 获取对应的D1表名
        const d1TableName = collection;
        
        if (Array.isArray(dataToExport)) {
          if (dataToExport.length === 0) {
            console.log(`${collection}数据为空数组，跳过`);
            continue;
          }
          
          // 检查数据中是否所有记录都有id字段
          const missingIds = dataToExport.filter((item: any) => !item.id);
          if (missingIds.length > 0) {
            console.log(`${collection}中有${missingIds.length}条记录缺少ID字段，将自动添加`);
            // 为缺少ID的记录添加UUID
            missingIds.forEach((item: any) => {
              item.id = crypto.randomUUID ? crypto.randomUUID() : `${collection}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
            });
          }
          
          console.log(`开始上传${collection}数据: ${dataToExport.length}条记录 (${useIncremental ? '增量' : '全量'}模式)`);
          
          // 计算数据大小（粗略估计）
          const dataSize = JSON.stringify(dataToExport).length;
          const dataSizeMB = dataSize / (1024 * 1024);
          
          // 根据数据大小判断是否为大型集合
          const isBigCollection = dataToExport.length > 500 || dataSizeMB > 1;
          
          // 如果是大型集合或数据较大，分批上传
          if (isBigCollection || dataSizeMB > 5) {
            // 使用固定批次大小
            const batchSize = fixedBatchSize;
            
            console.log(`${collection}是大型集合，采用批次上传策略，每批${batchSize}条记录，总数据大小约${dataSizeMB.toFixed(2)}MB`);
            let successBatchCount = 0;
            let totalProcessed = 0;
            const totalBatches = Math.ceil(dataToExport.length / batchSize);
            
            // 更新当前集合信息
            syncProgressState.currentCollection = collection;
            syncProgressState.totalBatches = totalBatches;
            
            // 分批上传数据
            for (let i = 0; i < dataToExport.length; i += batchSize) {
              syncProgressState.currentBatch++;
              
              const batchData = dataToExport.slice(i, i + batchSize);
              const batchNumber = Math.floor(i / batchSize) + 1;
                              const progress = Math.round((i / dataToExport.length) * 100);
                console.log(`上传${collection}批次 ${batchNumber}/${totalBatches} (${progress}%), 批次大小: ${batchData.length}`);
                
                // 跟踪批次统计
                totalBatchesProcessed++;
                sumBatchSize += batchData.length;
                batchingStats.largestBatchSize = Math.max(batchingStats.largestBatchSize, batchData.length);
                batchingStats.smallestBatchSize = Math.min(batchingStats.smallestBatchSize, batchData.length);
                
                // 添加重试机制
                let retryCount = 0;
                let success = false;
                
                while (!success && retryCount <= maxRetries) {
                  try {
                    // 如果是重试，添加延迟
                    if (retryCount > 0) {
                      const retryDelay = retryDelayBase * Math.pow(1.5, retryCount - 1);
                      console.log(`重试上传${collection}批次${batchNumber}，第${retryCount}次重试，等待${retryDelay}ms...`);
                      await new Promise(resolve => setTimeout(resolve, retryDelay));
                      totalRetries++;
                    }
                    
                    // 增量模式时使用不同的API端点
                    const apiEndpoint = useIncremental 
                      ? `https://akayd1.ggff.net/api/d1/${d1TableName}/incremental-batch`
                      : `https://akayd1.ggff.net/api/d1/${d1TableName}/batch`;
                    
                    // 准备请求头
                    const batchHeaders: any = {
                      ...sessionAuthHeaders,
                      'Content-Type': 'application/json'
                    };
                    
                    // 只有全量模式且是第一批时才清空表
                    if (!useIncremental && batchNumber === 1) {
                      batchHeaders['X-Clear-Table'] = 'true';
                      console.log(`${collection}全量模式第一批，将清空D1数据库中的现有数据`);
                    }
                    
                    // 上传当前批次数据，使用会话认证头避免重复获取
                    const response = await fetch(apiEndpoint, {
                      method: 'POST',
                      headers: batchHeaders,
                      body: JSON.stringify(batchData)
                    });
                    
                    if (response.ok) {
                      await response.json();
                      successBatchCount++;
                      totalProcessed += batchData.length;
                      totalRecords += batchData.length;
                      
                      const newProgress = Math.round((totalProcessed / dataToExport.length) * 100);
                      console.log(`批次${batchNumber}上传成功: ${batchData.length}条记录，总进度: ${totalProcessed}/${dataToExport.length} (${newProgress}%)`);
                      
                      success = true;
                    } else {
                      const errorText = await response.text();
                      throw new Error(`HTTP ${response.status}: ${errorText}`);
                    }
                  } catch (batchError) {
                    console.error(`批次${batchNumber}上传失败:`, batchError);
                    
                    syncProgressState.errorDetails.push({
                      collection,
                      batch: batchNumber,
                      error: batchError instanceof Error ? batchError.message : String(batchError),
                      timestamp: new Date().toISOString()
                    });
                    
                    if (retryCount < maxRetries) {
                      retryCount++;
                    } else {
                      console.error(`处理${collection}批次${batchNumber}失败，已达到最大重试次数`);
                      success = true; // 停止重试
                    }
                  }
                }
                
                // 优化延迟策略：根据性能动态调整
                if (i + batchSize < dataToExport.length) {
                  // 降低延迟，提高整体同步速度
                  const baseDelay = Math.max(10, Math.min(200, Math.round(dataSizeMB * 5)));
                  const adaptiveDelay = retryCount > 0 ? baseDelay * 2 : baseDelay; // 失败时增加延迟
                  console.log(`添加${adaptiveDelay}ms延迟...`);
                  await new Promise(resolve => setTimeout(resolve, adaptiveDelay));
                }
              }
              
              // 检查是否所有批次都成功
              if (successBatchCount === totalBatches) {
                console.log(`${collection}全部${successBatchCount}个批次上传成功，共${dataToExport.length}条记录`);
                successCount++;
              } else {
                // 部分批次失败
                const successPercentage = Math.round((totalProcessed / dataToExport.length) * 100);
                console.log(`${collection}部分上传成功: ${successBatchCount}/${totalBatches}批次，${totalProcessed}/${dataToExport.length}条记录 (${successPercentage}%)`);
                
                // 如果大部分都成功了，我们仍然视为整体成功
                if (successPercentage >= 90) {
                  successCount++;
                } else {
                  errorCount++;
                  failedCollections.push(collection);
                  errors.push({
                    collection,
                    error: `仅完成了${successPercentage}%的上传，${totalBatches - successBatchCount}个批次失败`
                  });
                }
              }
          } else {
            // 对于小型集合，一次性上传
            console.log(`${collection}是小型集合，一次性上传${dataToExport.length}条记录 (${useIncremental ? '增量' : '全量'}模式)`);
            
            // 更新当前集合信息
            syncProgressState.currentCollection = collection;
            syncProgressState.totalBatches = 1;
            syncProgressState.currentBatch = 1;
            
            // 跟踪批次统计
            totalBatchesProcessed++;
            sumBatchSize += dataToExport.length;
            batchingStats.largestBatchSize = Math.max(batchingStats.largestBatchSize, dataToExport.length);
            batchingStats.smallestBatchSize = Math.min(batchingStats.smallestBatchSize, dataToExport.length);
            
            try {
              // 上传数据，使用会话认证头
              const headers: any = {
                ...sessionAuthHeaders,
                'Content-Type': 'application/json'
              };
              
              // 只有全量模式时才清空表
              if (!useIncremental) {
                headers['X-Clear-Table'] = 'true';
              }
              
              // 选择合适的API端点
              const apiEndpoint = useIncremental 
                ? `https://akayd1.ggff.net/api/d1/${d1TableName}/incremental-batch`
                : `https://akayd1.ggff.net/api/d1/${d1TableName}/batch`;
              
              const response = await fetch(apiEndpoint, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(dataToExport)
              });
              
              if (response.ok) {
                await response.json();
                totalRecords += dataToExport.length;
                successCount++;
                console.log(`${collection}上传成功: ${dataToExport.length}条记录`);
              } else {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
              }
            } catch (error) {
              console.error(`${collection}上传失败:`, error);
              errorCount++;
              failedCollections.push(collection);
              errors.push({
                collection,
                error: error instanceof Error ? error.message : String(error)
              });
            }
          }
        } else if (typeof dataToExport === 'object') {
          // 处理非数组对象数据（如current_context、preferences等）
          try {
            // 添加id字段和时间戳如果不存在
            const dataWithId = { 
              ...dataToExport,
              id: dataToExport.id || collection, // 使用集合名作为ID
              last_modified_locally: new Date().toISOString()
            };
            
            // 选择合适的API端点
            const apiEndpoint = useIncremental 
              ? `https://akayd1.ggff.net/api/d1/${d1TableName}/incremental`
              : `https://akayd1.ggff.net/api/d1/${d1TableName}`;
            
            // 准备请求头
            const objHeaders: any = {
              ...sessionAuthHeaders,
              'Content-Type': 'application/json'
            };
            
            // 只有全量模式时才清空表
            if (!useIncremental) {
              objHeaders['X-Clear-Table'] = 'true';
            }
            
            const response = await fetch(apiEndpoint, {
              method: 'POST',
              headers: objHeaders,
              body: JSON.stringify(dataWithId)
            });
            
            if (response.ok) {
              await response.json();
              totalRecords++;
              successCount++;
              syncProgressState.current++;
              console.log(`成功导出${collection}数据对象到D1数据库 (${useIncremental ? '增量' : '全量'}模式)`);
            } else {
              console.error(`导出${collection}对象数据时出错:`, await response.text());
              errorCount++;
              failedCollections.push(collection);
              errors.push({
                collection,
                error: await response.text(),
                status: response.status
              });
              
              syncProgressState.errorDetails.push({
                collection,
                error: `导出对象数据失败: HTTP ${response.status}`,
                timestamp: new Date().toISOString()
              });
            }
          } catch (objError) {
            console.error(`处理${collection}对象数据时出错:`, objError);
            errorCount++;
            failedCollections.push(collection);
            errors.push({
              collection,
              error: objError instanceof Error ? objError.message : String(objError)
            });
            
            syncProgressState.errorDetails.push({
              collection,
              error: objError instanceof Error ? objError.message : String(objError),
              timestamp: new Date().toISOString()
            });
          }
        }
        
        // 记录导出时间戳（只有在成功导出后才记录）
        if (successCount > 0) {
          lastExportTimestamps[collection] = new Date().toISOString();
        }
      } catch (error) {
        errorCount++;
        failedCollections.push(collection);
        console.error(`处理${collection}数据时出错:`, error);
        errors.push({
          collection,
          error: error instanceof Error ? error.message : String(error)
        });
        
        syncProgressState.errorDetails.push({
          collection,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString()
        });
      }
    }
    
    // 计算压缩统计
    if (compressionStats.totalSizeBefore > 0 && compressionStats.totalSizeAfter > 0) {
      compressionStats.compressionRatio = compressionStats.totalSizeAfter / compressionStats.totalSizeBefore;
    }
    
    // 计算批处理统计
    if (totalBatchesProcessed > 0) {
      batchingStats.averageBatchSize = sumBatchSize / totalBatchesProcessed;
      batchingStats.totalBatches = totalBatchesProcessed;
    }
    
    // 保存导出时间戳
    saveTimestamps();
    
    // 🔧 修复：清除所有缓存，确保导出后数据一致性
    if (successCount > 0) {
      console.log('🧹 清除缓存，确保导出后数据同步...');
      try {
        // 导入StorageManager来清除缓存
        const { storageManager } = await import('./storage');
        storageManager.clearAllCache();
        console.log('✅ 导出后缓存清除完成');
      } catch (error) {
        console.error('清除缓存失败:', error);
        // 不影响主要功能，只记录错误
      }
    }
    
    // 可选的一致性检查
    let consistencyResults: any = {};
    if (checkConsistency && successCount > 0) {
      console.log('执行导出后一致性检查...');
      for (const collection of collections) {
        if (!failedCollections.includes(collection)) {
          try {
            const consistencyCheck = await checkDataConsistency(collection);
            consistencyResults[collection] = consistencyCheck;
            if (!consistencyCheck.consistent) {
              console.warn(`${collection}导出后一致性检查失败，发现${consistencyCheck.conflicts.length}个冲突`);
            } else {
              console.log(`${collection}导出后一致性检查通过`);
            }
          } catch (error) {
            console.warn(`${collection}一致性检查失败:`, error);
          }
        }
      }
    }
    
    // 更新最终状态
    syncProgressState.status = 'completed';
    syncProgressState.endTime = Date.now();
    
    // 生成结果消息
    let resultMessage = `${useIncremental ? '增量' : '全量'}数据导出完成！成功导出${successCount}个集合，共${totalRecords}条记录`;
    if (errorCount > 0) {
      resultMessage += `，${errorCount}个集合失败 (${failedCollections.join(', ')})`;
    }
    if (useIncremental && totalRecords === 0) {
      resultMessage += '，无变更数据需要导出';
    }
    
    return {
      success: errorCount === 0,
      message: resultMessage,
      stats: { 
        totalRecords, 
        successCount, 
        errorCount,
        failedCollections: failedCollections.length > 0 ? failedCollections : undefined,
        errors: errors.length > 0 ? errors : undefined,
        retryCount: totalRetries,
        compressionEnabled: useCompression,
        compressionStats: useCompression ? compressionStats : undefined,
        batchingStats,
        consistencyCheck: checkConsistency ? consistencyResults : undefined
      }
    };
  } catch (error) {
    // 更新错误状态
    syncProgressState.status = 'error';
    syncProgressState.endTime = Date.now();
    
    return {
      success: false,
      message: `导出数据到D1失败: ${error instanceof Error ? error.message : '未知错误'}`,
      stats: { totalRecords: 0, successCount: 0, errorCount: 0 }
    };
  } finally {
    isExportingData = false;
  }
};

// 新增：快捷增量同步函数
export const incrementalImport = async (options: {
  checkConsistency?: boolean;
  conflictResolution?: 'use_local' | 'use_remote' | 'use_newer';
} = {}) => {
  return importFromD1(true, options);
};

export const incrementalExport = async (options: {
  useCompression?: boolean;
  checkConsistency?: boolean;
} = {}) => {
  return exportToD1({
    ...options,
    useIncremental: true
  });
};

// 新增：双向增量同步
export const incrementalSync = async (options: {
  checkConsistency?: boolean;
  conflictResolution?: 'use_local' | 'use_remote' | 'use_newer';
} = {}) => {
  try {
    console.log('开始双向增量同步...');
    
    // 首先进行增量导入
    console.log('步骤1: 从远程导入增量数据');
    const importResult = await incrementalImport(options);
    
    if (!importResult.success) {
      console.error('增量导入失败:', importResult.message);
      return {
        success: false,
        message: `增量同步失败: 导入阶段出错 - ${importResult.message}`,
        importResult,
        exportResult: null
      };
    }
    
    // 然后进行增量导出
    console.log('步骤2: 向远程导出增量数据');
    const exportResult = await incrementalExport(options);
    
    if (!exportResult.success) {
      console.error('增量导出失败:', exportResult.message);
      return {
        success: false,
        message: `增量同步失败: 导出阶段出错 - ${exportResult.message}`,
        importResult,
        exportResult
      };
    }
    
    const totalImported = importResult.stats.totalRecords;
    const totalExported = exportResult.stats.totalRecords;
    
    return {
      success: true,
      message: `增量同步完成！导入${totalImported}条记录，导出${totalExported}条记录`,
      importResult,
      exportResult
    };
  } catch (error) {
    return {
      success: false,
      message: `增量同步异常: ${error instanceof Error ? error.message : '未知错误'}`,
      importResult: null,
      exportResult: null
    };
  }
};

// 新增：数据一致性检查函数
export const checkAllDataConsistency = async (): Promise<{
  overallConsistent: boolean;
  results: { [collection: string]: any };
  summary: {
    totalCollections: number;
    consistentCollections: number;
    inconsistentCollections: number;
    totalConflicts: number;
  };
}> => {
  const collections = [
    'students', 'classes', 'attendance', 'teacher_attendance', 'grades',
    'payments', 'subjects', 'schoolYears', 'teachers',
    'custom_reports', 'alert_rules', 'preferences', 'custom_exams',
    'current_context'
  ];
  
  const results: { [collection: string]: any } = {};
  let totalConflicts = 0;
  let consistentCollections = 0;
  
  for (const collection of collections) {
    try {
      const result = await checkDataConsistency(collection);
      results[collection] = result;
      
      if (result.consistent) {
        consistentCollections++;
      } else {
        totalConflicts += result.conflicts.length;
      }
    } catch (error) {
      results[collection] = {
        consistent: false,
        error: error instanceof Error ? error.message : String(error),
        localCount: 0,
        remoteCount: 0,
        conflicts: []
      };
    }
  }
  
  return {
    overallConsistent: totalConflicts === 0,
    results,
    summary: {
      totalCollections: collections.length,
      consistentCollections,
      inconsistentCollections: collections.length - consistentCollections,
      totalConflicts
    }
  };
};

// 新增：获取同步时间戳信息
export const getSyncTimestamps = () => {
  return {
    lastSyncTimestamps: { ...lastSyncTimestamps },
    lastExportTimestamps: { ...lastExportTimestamps }
  };
};

// 新增：重置同步时间戳
export const resetSyncTimestamps = (type: 'import' | 'export' | 'all' = 'all') => {
  if (type === 'import' || type === 'all') {
    lastSyncTimestamps = {};
  }
  if (type === 'export' || type === 'all') {
    lastExportTimestamps = {};
  }
  saveTimestamps();
  console.log(`已重置${type === 'all' ? '所有' : type === 'import' ? '导入' : '导出'}时间戳`);
};

// 创建数据同步管理器
export const dataSyncManager = {
  // 基础功能
  importFromD1,
  exportToD1,
  getStatus: getDataSyncStatus,
  cancelSync: cancelDataSync,
  
  // 增量同步功能
  incrementalImport,
  incrementalExport,
  incrementalSync,
  
  // 一致性检查
  checkDataConsistency,
  checkAllDataConsistency,
  
  // 时间戳管理
  getSyncTimestamps,
  resetSyncTimestamps,
  
  // 数据合并工具
  mergeData,
  mergeDataWithConflictResolution,
  
  // 认证管理
  clearAuthCache,
  checkAuthStatus,
  getBatchAuthorizationHeader,
  refreshAuthCache: () => {
    console.log('🔄 刷新认证缓存...');
    clearAuthCache();
    getAuthorizationHeader(false);
  }
}; 